"use client";

import { useEffect, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  fromHijri,
  GREGORIAN_REFORM_DAYS,
  type HijriCalendar,
  type HijriDate,
  inUmmAlQuraTable,
  isCalendarSupported,
  julianFromDays,
  MAX_HIJRI_YEAR,
  monthLength,
  monthsOfYear,
  parseGregorianDate,
  toHijri,
  yearSpan,
} from "@/lib/tool-kit/converters/hijri";
import {
  daysFromCivil,
  dayToUtcDate,
  toIsoDate,
} from "@/lib/tool-kit/converters/week-number";

/** The month names in Arabic script, as CLDR writes them for `ar-SA`. Data, not prose. */
const ARABIC_MONTHS = [
  "محرم",
  "صفر",
  "ربيع الأول",
  "ربيع الآخر",
  "جمادى الأولى",
  "جمادى الآخرة",
  "رجب",
  "شعبان",
  "رمضان",
  "شوال",
  "ذو القعدة",
  "ذو الحجة",
] as const;

/** English strings. A translated content file overrides these through `ui`. */
export const HIJRI_DATE_CONVERTER_STRINGS = {
  calendar: "Calendar",
  ummAlQura: "Umm al-Qura (Saudi Arabia)",
  civil: "Tabular (arithmetical)",
  ummAlQuraInline: "Umm al-Qura",
  civilInline: "tabular",
  direction: "Convert",
  toHijri: "Gregorian → Hijri",
  toGregorian: "Hijri → Gregorian",
  gregorianDate: "Gregorian date",
  today: "Today",
  hijriDay: "Day",
  hijriMonth: "Month",
  hijriYear: "Year (AH)",
  monthNames: [
    "Muharram",
    "Safar",
    "Rabiʿ al-Awwal",
    "Rabiʿ al-Thani",
    "Jumada al-Ula",
    "Jumada al-Akhira",
    "Rajab",
    "Shaʿban",
    "Ramadan",
    "Shawwal",
    "Dhu al-Qaʿda",
    "Dhu al-Hijja",
  ],
  hijriDate: "{day} {month} {year} AH",
  hijriLabel: "Hijri date",
  arabicLabel: "In Arabic",
  weekdayLabel: "Day of the week",
  gregorianLabel: "Gregorian date",
  isoLabel: "ISO 8601",
  julianLabel: "Julian calendar",
  otherCalendar: "In the {calendar} calendar the same day is {date}.",
  sameInOther: "The {calendar} calendar gives the same date.",
  monthSpan: "{month} {year} AH has {length} days: {start} to {end}.",
  yearSpan: "The year {year} AH has {length} days: {start} to {end}.",
  outsideTable:
    "The Umm al-Qura tables built into browsers cover AH 1300–1600 (1882–2174 CE). Outside that range this result is the tabular calendar's.",
  julianNote:
    "Dates before 15 October 1582 are entered and shown in the proleptic Gregorian calendar; historians usually quote the Julian date shown here.",
  sightingNote:
    "These are calculated dates. Where a month begins with a sighting of the new crescent — as Ramadan and the two Eids usually do — the observed date can be a day or two different, so check a local announcement for religious dates.",
  pickDate: "Pick a date to convert.",
  invalidGregorian:
    "Choose a real date between 19 July 622 (1 Muharram AH 1) and 31 December 9999.",
  enterHijri: "Enter a Hijri day, month and year.",
  invalidHijriYear: "Enter a Hijri year from 1 to 9600.",
  noSuchDay:
    "{month} {year} AH has only {length} days in this calendar, so day {day} does not exist.",
  unsupported:
    "This browser's date library does not include this calendar. Try a current version of Chrome, Edge, Firefox or Safari.",
  tableHeading: "The months of {year} AH",
  monthColumn: "Month",
  arabicColumn: "Arabic",
  startsColumn: "Begins",
  daysColumn: "Days",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{hijri} = {gregorian}",
  numberedMonth: "{number}. {name}",
} as const;

type Strings = typeof HIJRI_DATE_CONVERTER_STRINGS;
type Direction = "to-hijri" | "to-gregorian";

const LONG_DATE: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
};

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
};

function otherOf(calendar: HijriCalendar): HijriCalendar {
  return calendar === "islamic-umalqura" ? "islamic-civil" : "islamic-umalqura";
}

/** The calendar's name inside a sentence ("In the tabular calendar …"). */
function calendarName(calendar: HijriCalendar, strings: Strings): string {
  return calendar === "islamic-umalqura"
    ? strings.ummAlQuraInline
    : strings.civilInline;
}

/**
 * The date as an Arabic reader writes it, whatever the page language — the form printed on
 * an Arabic book or document, which is what a reader is usually matching.
 */
function arabicDate(days: number, calendar: HijriCalendar): string {
  try {
    return new Intl.DateTimeFormat(`ar-SA-u-ca-${calendar}`, {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(dayToUtcDate(days));
  } catch {
    return "";
  }
}

function useFormatters(strings: Strings) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return {
    long: (days: number) =>
      formatDateTime(locale, dayToUtcDate(days), LONG_DATE),
    short: (days: number) =>
      formatDateTime(locale, dayToUtcDate(days), SHORT_DATE),
    hijri: (date: HijriDate) =>
      t(strings.hijriDate, {
        day: fmt(date.day),
        month: strings.monthNames[date.month - 1] ?? "",
        year: String(date.year),
      }),
    arabic: (days: number, calendar: HijriCalendar) =>
      arabicDate(days, calendar),
    weekday: (days: number) =>
      formatDateTime(locale, dayToUtcDate(days), {
        weekday: "long",
        timeZone: "UTC",
      }),
    julian: (days: number) => {
      const j = julianFromDays(days);
      return formatDateTime(
        locale,
        dayToUtcDate(daysFromCivil(j.year, j.month, j.day)),
        { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }
      );
    },
  };
}

/** The sentences under the result: the other calendar, the month, the year, the caveats. */
function contextLines(
  days: number,
  calendar: HijriCalendar,
  strings: Strings,
  format: ReturnType<typeof useFormatters>,
  fmt: (value: number) => string
): string[] {
  const hijri = toHijri(days, calendar);
  const other = otherOf(calendar);
  const otherDate = toHijri(days, other);
  const same =
    otherDate.year === hijri.year &&
    otherDate.month === hijri.month &&
    otherDate.day === hijri.day;
  const lines = [
    same
      ? t(strings.sameInOther, { calendar: calendarName(other, strings) })
      : t(strings.otherCalendar, {
          calendar: calendarName(other, strings),
          date: format.hijri(otherDate),
        }),
  ];
  const months = monthsOfYear(hijri.year, calendar);
  const month = months[hijri.month - 1];
  lines.push(
    t(strings.monthSpan, {
      month: strings.monthNames[hijri.month - 1] ?? "",
      year: String(hijri.year),
      length: fmt(month.length),
      start: format.short(month.start),
      end: format.short(month.start + month.length - 1),
    })
  );
  const span = yearSpan(hijri.year, calendar);
  lines.push(
    t(strings.yearSpan, {
      year: String(hijri.year),
      length: fmt(span.length),
      start: format.short(span.start),
      end: format.short(span.end),
    })
  );
  if (calendar === "islamic-umalqura" && !inUmmAlQuraTable(hijri.year)) {
    lines.push(strings.outsideTable);
  }
  if (days < GREGORIAN_REFORM_DAYS) {
    lines.push(strings.julianNote);
  }
  return lines;
}

function ResultPanel({
  days,
  calendar,
  direction,
  strings,
}: {
  days: number;
  calendar: HijriCalendar;
  direction: Direction;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const format = useFormatters(strings);
  const hijri = toHijri(days, calendar);
  const hijriText = format.hijri(hijri);
  const gregorianText = format.long(days);
  const lines = contextLines(days, calendar, strings, format, fmt);
  const early = days < GREGORIAN_REFORM_DAYS;
  const months = monthsOfYear(hijri.year, calendar);
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {direction === "to-hijri" ? hijriText : gregorianText}
        </h2>
        <StatGrid>
          <StatCard
            emphasis={direction === "to-hijri"}
            label={strings.hijriLabel}
            value={hijriText}
          />
          <StatCard
            label={strings.arabicLabel}
            value={<span dir="rtl">{format.arabic(days, calendar)}</span>}
          />
          <StatCard
            emphasis={direction === "to-gregorian"}
            label={strings.gregorianLabel}
            value={format.short(days)}
          />
          <StatCard label={strings.weekdayLabel} value={format.weekday(days)} />
          <StatCard label={strings.isoLabel} value={toIsoDate(days)} />
          {early ? (
            <StatCard label={strings.julianLabel} value={format.julian(days)} />
          ) : null}
        </StatGrid>
        <ul className="space-y-1 text-foreground text-sm leading-relaxed">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copySummary, {
            hijri: hijriText,
            gregorian: gregorianText,
          })}
        />
      </section>
      <Notice>{strings.sightingNote}</Notice>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>
          {t(strings.tableHeading, { year: String(hijri.year) })}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className={TABLE}>
            <thead>
              <tr>
                <th className={TH} scope="col">
                  {strings.monthColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.arabicColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.startsColumn}
                </th>
                <th className={TH} scope="col">
                  {strings.daysColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {months.map((month) => (
                <tr
                  className={
                    month.month === hijri.month
                      ? "bg-primary/10 font-medium"
                      : ""
                  }
                  key={month.month}
                >
                  <td className={TD}>
                    {t(strings.numberedMonth, {
                      number: fmt(month.month),
                      name: strings.monthNames[month.month - 1] ?? "",
                    })}
                  </td>
                  <td className={TD} dir="rtl" lang="ar">
                    {ARABIC_MONTHS[month.month - 1]}
                  </td>
                  <td className={TD}>{format.short(month.start)}</td>
                  <td className={TD}>{fmt(month.length)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

interface HijriFields {
  day: string;
  month: string;
  year: string;
}

type Reading = { days: number } | { error: string } | null;

function readHijri(
  fields: HijriFields,
  calendar: HijriCalendar,
  strings: Strings,
  fmt: (value: number) => string
): Reading {
  if (fields.day.trim() === "" || fields.year.trim() === "") {
    return null;
  }
  const day = parseIntegerInput(fields.day);
  const month = Number(fields.month);
  const year = parseIntegerInput(fields.year);
  if (year === null || year < 1 || year > MAX_HIJRI_YEAR) {
    return { error: strings.invalidHijriYear };
  }
  if (day === null || day < 1 || day > 30) {
    return { error: strings.enterHijri };
  }
  const days = fromHijri({ year, month, day }, calendar);
  if (days === null) {
    return {
      error: t(strings.noSuchDay, {
        month: strings.monthNames[month - 1] ?? "",
        year: String(year),
        length: fmt(monthLength(year, month, calendar)),
        day: fmt(day),
      }),
    };
  }
  return { days };
}

function readGregorian(text: string, strings: Strings): Reading {
  if (text.trim() === "") {
    return null;
  }
  const days = parseGregorianDate(text);
  return days === null ? { error: strings.invalidGregorian } : { days };
}

/** Today on the reader's own clock — only ever called after hydration. */
function todayDays(): number {
  const now = new Date();
  return daysFromCivil(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export default function HijriDateConverterWidget() {
  const strings = useToolUi(HIJRI_DATE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [calendar, setCalendar] = useState<HijriCalendar>("islamic-umalqura");
  const [direction, setDirection] = useState<Direction>("to-hijri");
  const [gregorian, setGregorian] = useState("");
  const [fields, setFields] = useState<HijriFields>({
    day: "",
    month: "1",
    year: "",
  });
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    setSupported(
      isCalendarSupported("islamic-umalqura") &&
        isCalendarSupported("islamic-civil")
    );
    const today = todayDays();
    const hijri = toHijri(today, "islamic-umalqura");
    setGregorian((current) => current || toIsoDate(today));
    setFields((current) =>
      current.year === ""
        ? {
            day: String(hijri.day),
            month: String(hijri.month),
            year: String(hijri.year),
          }
        : current
    );
  }, []);

  const reading =
    direction === "to-hijri"
      ? readGregorian(gregorian, strings)
      : readHijri(fields, calendar, strings, fmt);

  const setField = (key: keyof HijriFields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <PillGroup
          label={strings.calendar}
          name="hd-calendar"
          onChange={setCalendar}
          options={[
            { value: "islamic-umalqura", label: strings.ummAlQura },
            { value: "islamic-civil", label: strings.civil },
          ]}
          value={calendar}
        />
        <PillGroup
          label={strings.direction}
          name="hd-direction"
          onChange={setDirection}
          options={[
            { value: "to-hijri", label: strings.toHijri },
            { value: "to-gregorian", label: strings.toGregorian },
          ]}
          value={direction}
        />
      </div>
      {direction === "to-hijri" ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <TextField
              id="hd-gregorian"
              label={strings.gregorianDate}
              onChange={setGregorian}
              type="date"
              value={gregorian}
            />
          </div>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setGregorian(toIsoDate(todayDays()))}
            type="button"
          >
            {strings.today}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[1fr_2fr_1fr]">
          <TextField
            id="hd-day"
            label={strings.hijriDay}
            numeric
            onChange={setField("day")}
            value={fields.day}
          />
          <SelectField
            id="hd-month"
            label={strings.hijriMonth}
            onChange={setField("month")}
            options={strings.monthNames.map((name, index) => ({
              value: String(index + 1),
              label: t(strings.numberedMonth, { number: fmt(index + 1), name }),
            }))}
            value={fields.month}
          />
          <TextField
            id="hd-year"
            label={strings.hijriYear}
            numeric
            onChange={setField("year")}
            value={fields.year}
          />
        </div>
      )}
      {supported ? null : <ErrorNotice>{strings.unsupported}</ErrorNotice>}
      {reading === null ? (
        <Notice>
          {direction === "to-hijri" ? strings.pickDate : strings.enterHijri}
        </Notice>
      ) : null}
      {reading !== null && "error" in reading ? (
        <ErrorNotice>{reading.error}</ErrorNotice>
      ) : null}
      {supported && reading !== null && "days" in reading ? (
        <ResultPanel
          calendar={calendar}
          days={reading.days}
          direction={direction}
          strings={strings}
        />
      ) : null}
    </div>
  );
}
